import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, EyeOff, ExternalLink, Image as ImageIcon, ShieldCheck, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminPortfolio() {
  const [settings, setSettings] = useState({ publicar_sin_aprobacion: false, excluidas: [] });
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState("todas"); // todas | publicadas | excluidas | sin_aprobar

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/portfolio/fotos");
      setSettings(data.settings || { publicar_sin_aprobacion: false, excluidas: [] });
      setFotos(data.fotos || []);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleSetting = async (val) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/portfolio/settings", { publicar_sin_aprobacion: val });
      setSettings(data);
      toast.success(val ? "Publicación sin aprobación: ACTIVADA" : "Publicación sin aprobación: DESACTIVADA");
      await load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const excluir = async (url) => {
    try {
      await api.post("/admin/portfolio/excluir", { url });
      toast.success("Foto retirada del portfolio");
      await load();
    } catch (e) { toast.error("Error: " + (e?.response?.data?.detail || e.message)); }
  };
  const incluir = async (url) => {
    try {
      await api.post("/admin/portfolio/incluir", { url });
      toast.success("Foto restaurada al portfolio");
      await load();
    } catch (e) { toast.error("Error: " + (e?.response?.data?.detail || e.message)); }
  };

  const filtered = fotos.filter((f) => {
    if (filtro === "publicadas") return f.publicada;
    if (filtro === "excluidas") return f.excluida;
    if (filtro === "sin_aprobar") return !f.aprobada_cliente && !f.excluida;
    return true;
  });

  const stats = {
    total: fotos.length,
    publicadas: fotos.filter((f) => f.publicada).length,
    excluidas: fotos.filter((f) => f.excluida).length,
    sin_aprobar: fotos.filter((f) => !f.aprobada_cliente).length,
  };

  return (
    <div className="space-y-5" data-testid="page-admin-portfolio">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="bg-violet-500/15 border border-violet-500/30 rounded-lg p-2.5">
            <ImageIcon className="w-6 h-6 text-violet-300" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-zinc-100">Portfolio público</h1>
            <p className="text-sm text-zinc-400 mt-0.5">Control de qué fotos aparecen en <a href="/portfolio" target="_blank" rel="noreferrer" className="text-violet-300 underline">/portfolio</a>.</p>
          </div>
        </div>
        <Link to="/portfolio" target="_blank" rel="noreferrer"
          className="text-sm text-violet-300 hover:text-violet-200 inline-flex items-center gap-1.5">
          Ver portfolio público <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* Toggle global */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-zinc-300 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-300" /> Política de publicación
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start justify-between gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3">
            <div className="flex-1">
              <div className="text-sm text-zinc-200 font-medium">Publicar fotos sin esperar aprobación del cliente</div>
              <div className="text-xs text-zinc-500 mt-0.5 leading-snug max-w-2xl">
                Si activas esta opción, todas las fotos finales de las paradas montadas se publican en el portfolio sin requerir el botón
                "Aprobar" del cliente en el portal. Útil para activar el portfolio inmediatamente. Si un cliente solicita retirar una foto,
                puedes ocultarla individualmente desde la lista de abajo.
              </div>
            </div>
            <Switch checked={settings.publicar_sin_aprobacion}
              onCheckedChange={toggleSetting} disabled={saving}
              data-testid="toggle-publicar-sin-aprobacion" />
          </div>
          {settings.publicar_sin_aprobacion ? (
            <div className="text-xs text-emerald-300 bg-emerald-500/[0.05] border border-emerald-500/20 rounded px-3 py-2">
              ✓ Modo activo: las fotos se publican automáticamente al finalizar el montaje (salvo que estén en la lista de excluidas).
            </div>
          ) : (
            <div className="text-xs text-amber-300 bg-amber-500/[0.05] border border-amber-500/20 rounded px-3 py-2">
              ⏳ Modo conservador: las fotos solo se publican cuando el cliente las aprueba desde su portal.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats + filtros */}
      <div className="flex flex-wrap gap-2">
        {[
          { k: "todas", label: `Todas (${stats.total})`, c: "bg-zinc-800 border-zinc-700" },
          { k: "publicadas", label: `Publicadas (${stats.publicadas})`, c: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" },
          { k: "sin_aprobar", label: `Sin aprobar (${stats.sin_aprobar})`, c: "bg-amber-500/10 border-amber-500/30 text-amber-200" },
          { k: "excluidas", label: `Excluidas (${stats.excluidas})`, c: "bg-red-500/10 border-red-500/30 text-red-200" },
        ].map((b) => (
          <button key={b.k} onClick={() => setFiltro(b.k)}
            data-testid={`filtro-${b.k}`}
            className={`text-xs px-3 py-1.5 rounded border ${b.c} ${filtro === b.k ? "ring-2 ring-violet-500/40" : ""}`}>
            {b.label}
          </button>
        ))}
      </div>

      {/* Galería */}
      {loading ? (
        <div className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Cargando…</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="py-10 text-center text-sm text-zinc-500">
            {stats.total === 0
              ? "No hay fotos finales en peticiones completed/cerrada/aprobada. Crea rutas con fotos finales y marca el cierre de la parada como montado."
              : "Ninguna foto coincide con este filtro."}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="fotos-grid">
          {filtered.map((f, i) => (
            <Card key={i} className="bg-zinc-900/60 border-zinc-800 overflow-hidden group"
              data-testid={`foto-card-${i}`}>
              <div className="aspect-square relative bg-zinc-900">
                <img src={f.url} alt={f.peticion_codigo} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-1 left-1 flex flex-col gap-1">
                  {f.publicada && <Badge className="bg-emerald-500/80 text-white text-[10px]">PÚBLICA</Badge>}
                  {f.excluida && <Badge className="bg-red-500/80 text-white text-[10px]">RETIRADA</Badge>}
                  {!f.aprobada_cliente && !f.excluida && <Badge className="bg-amber-500/80 text-white text-[10px]">SIN APROBAR</Badge>}
                </div>
              </div>
              <CardContent className="p-2 space-y-1.5">
                <div className="text-[11px] text-zinc-400 truncate">
                  <Link to={`/peticiones/${f.peticion_id}`} target="_blank" className="hover:text-amber-300">
                    {f.peticion_codigo || "—"}
                  </Link>
                </div>
                <div className="text-[10px] text-zinc-500 truncate">{f.cliente_nombre || "—"}</div>
                {f.excluida ? (
                  <Button onClick={() => incluir(f.url)} size="sm"
                    data-testid={`btn-incluir-${i}`}
                    className="w-full h-7 text-[11px] bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/40">
                    <Eye className="w-3 h-3 mr-1" /> Restaurar
                  </Button>
                ) : (
                  <Button onClick={() => excluir(f.url)} size="sm" variant="outline"
                    data-testid={`btn-excluir-${i}`}
                    className="w-full h-7 text-[11px] bg-transparent hover:bg-red-500/10 text-red-300 border-red-500/30">
                    <EyeOff className="w-3 h-3 mr-1" /> Retirar
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
